import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { CompanySidebar } from '../components/Dashboard/CompanySidebar';
import { Plus, Briefcase, X, Calendar } from 'lucide-react';

interface CompanyJob {
    id: string;
    job_title: string;
    job_description: string;
    company_name?: string;
    created_at: string;
}

export const CompanyDashboardPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<CompanyJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [companyName, setCompanyName] = useState('');
    const [hirerType, setHirerType] = useState<'company' | 'recruiter'>('company');
    const [selectedJob, setSelectedJob] = useState<CompanyJob | null>(null);

    useEffect(() => {
        if (!user) return;

        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch hirer details
                const { data: hirerData } = await supabase
                    .from('hirers')
                    .select('id, name, hirer_type')
                    .eq('user_id', user.id)
                    .single();

                if (hirerData) {
                    setCompanyName(hirerData.name);
                    setHirerType(hirerData.hirer_type);

                    // Fetch jobs
                    const { data: jobsData } = await supabase
                        .from('hiring_jobs')
                        .select('*')
                        .eq('hirer_id', hirerData.id)
                        .order('created_at', { ascending: false });

                    if (jobsData) {
                        setJobs(jobsData);
                    }
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    // Skeleton components for loading state
    const HeaderSkeleton = () => (
        <div className="mb-10 animate-pulse">
            <div className="flex items-center gap-2 mb-2">
                <div className="h-9 bg-slate-200 rounded-lg w-40"></div>
                <div className="h-9 bg-slate-200 rounded-lg w-48"></div>
            </div>
            <div className="h-5 bg-slate-200 rounded w-80 mt-3"></div>
        </div>
    );

    const StatsSkeleton = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 animate-pulse">
                <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-slate-100 rounded-xl">
                        <div className="w-6 h-6 bg-slate-200 rounded"></div>
                    </div>
                </div>
                <div className="h-8 bg-slate-200 rounded w-12 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-32"></div>
            </div>
        </div>
    );

    const JobCardsSkeleton = () => (
        <div className="bg-slate-50/30 border border-slate-100 rounded-3xl p-6 md:p-8">
            <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex-shrink-0 w-[300px] md:w-[320px] bg-white rounded-2xl p-6 animate-pulse border border-slate-100">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-slate-100 rounded-xl">
                                <div className="w-6 h-6 bg-slate-200 rounded"></div>
                            </div>
                            <div className="h-5 bg-slate-100 rounded w-20"></div>
                        </div>
                        <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                        <div className="space-y-2 mb-6">
                            <div className="h-4 bg-slate-100 rounded w-full"></div>
                            <div className="h-4 bg-slate-100 rounded w-5/6"></div>
                        </div>
                        <div className="h-10 bg-slate-100 rounded w-full"></div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="dashboard-layout">
            <CompanySidebar />

            <div className="main-content">
                <div className="w-full max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8">
                    {/* Header Section */}
                    {loading ? (
                        <HeaderSkeleton />
                    ) : (
                        <div className="mb-6 md:mb-10 animate-fade-in">
                            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                                Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-700">{companyName}</span>
                            </h1>
                            <p className="text-sm md:text-base text-slate-600">Here's what's happening with your job postings today.</p>
                        </div>
                    )}

                    {/* Stats Grid */}
                    {loading ? (
                        <StatsSkeleton />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-10 animate-fade-in animate-delay-100">
                            <div className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-slate-100">
                                <div className="flex items-center justify-between mb-3 md:mb-4">
                                    <div className="p-2 md:p-3 bg-slate-100 rounded-xl">
                                        <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-slate-900" />
                                    </div>
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold text-slate-900">{jobs.length}</h3>
                                <p className="text-sm md:text-base text-slate-500">Active Job Postings</p>
                            </div>
                        </div>
                    )}

                    {/* Recent Job Postings */}
                    <div className="animate-fade-in animate-delay-200">
                        {loading ? (
                            <>
                                <div className="flex items-center justify-between mb-6 animate-pulse">
                                    <div className="h-7 bg-slate-200 rounded w-48"></div>
                                    <div className="h-10 bg-slate-200 rounded-xl w-36"></div>
                                </div>
                                <JobCardsSkeleton />
                            </>
                        ) : (
                            <>
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                                    <h2 className="text-lg md:text-xl font-bold text-slate-900">Recent Job Postings</h2>
                                    <button
                                        onClick={() => navigate('/job-postings')}
                                        className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-slate-900 text-white font-medium rounded-xl transition-colors shadow-lg shadow-black/20 text-sm md:text-base w-full sm:w-auto justify-center"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Post New Job
                                    </button>
                                </div>

                                {jobs.length > 0 ? (
                                    <div className="bg-slate-50/50 border border-slate-200 rounded-3xl p-6 md:p-8 overflow-hidden shadow-sm">
                                        <div className="flex overflow-x-auto gap-6 pb-2 scroll-smooth custom-scrollbar">
                                            {jobs.map((job) => (
                                                <div key={job.id} className="flex-shrink-0 w-[280px] sm:w-[320px] group bg-white p-5 rounded-2xl shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-500 hover:-translate-y-1">
                                                    <div className="flex justify-between items-start mb-4">
                                                        <div className="p-3 bg-slate-50 rounded-2xl transition-all duration-300">
                                                            <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-slate-700" />
                                                        </div>
                                                        <span className="text-[10px] md:text-xs font-bold text-slate-400 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100/50 uppercase tracking-wider">
                                                            {new Date(job.created_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                        </span>
                                                    </div>

                                                    {hirerType === 'recruiter' && job.company_name && (
                                                        <div className="mb-2">
                                                            <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100/50 uppercase tracking-tighter">
                                                                {job.company_name}
                                                            </span>
                                                        </div>
                                                    )}

                                                    <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2 line-clamp-1 group-hover:text-blue-600 transition-colors tracking-tight">{job.job_title}</h3>
                                                    <p className="text-slate-500 text-xs md:text-sm mb-6 line-clamp-2 h-8 md:h-10 leading-relaxed font-normal">{job.job_description}</p>

                                                    <button
                                                        onClick={() => setSelectedJob(job)}
                                                        className="w-full py-3 text-sm md:text-base text-slate-600 font-bold bg-slate-50 rounded-xl hover:bg-slate-900 hover:text-white transition-all duration-300 shadow-sm border border-slate-100"
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="flex-shrink-0 w-2 md:w-4" aria-hidden="true" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 md:py-12 bg-white rounded-2xl border border-slate-100 border-dashed">
                                        <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                                            <Briefcase className="w-6 h-6 md:w-8 md:h-8 text-slate-400" />
                                        </div>
                                        <h3 className="text-base md:text-lg font-medium text-slate-900 mb-2">No jobs posted yet</h3>
                                        <p className="text-sm md:text-base text-slate-500 mb-4 md:mb-6 px-4">Create your first job posting to start finding candidates</p>
                                        <button
                                            onClick={() => navigate('/job-postings')}
                                            className="px-6 py-2 bg-black hover:bg-slate-900 text-white font-medium rounded-xl transition-colors text-sm md:text-base"
                                        >
                                            Post a Job
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Job Details Modal */}
            {selectedJob && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-in">
                        {/* Header */}
                        <div className="flex-shrink-0 px-8 py-6 border-b border-slate-100 flex items-start justify-between bg-white sticky top-0 z-10">
                            <div>
                                <h2 className="text-3xl font-bold text-slate-900 mb-2">{selectedJob.job_title}</h2>
                                <div className="flex items-center gap-4 text-slate-500">
                                    {/* Company Name - Only show for recruiters */}
                                    {hirerType === 'recruiter' && selectedJob.company_name && (
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 bg-slate-100 rounded">
                                                <Briefcase className="w-4 h-4" />
                                            </div>
                                            <span className="font-semibold text-slate-700">
                                                {selectedJob.company_name}
                                            </span>
                                            <span className="text-slate-300">•</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span>
                                            Posted {new Date(selectedJob.created_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedJob(null)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-8">
                            <div className="max-w-3xl">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Briefcase className="w-4 h-4 text-slate-400" />
                                    Job Description
                                </h3>
                                <div className="prose prose-slate max-w-none">
                                    <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap font-light">
                                        {selectedJob.job_description}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 px-8 py-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setSelectedJob(null)}
                                className="px-6 py-3 font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedJob(null);
                                    navigate('/candidates');
                                }}
                                className="flex items-center gap-2 px-8 py-3 bg-black hover:bg-slate-900 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                            >
                                View Matched Candidates
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
